/**
 * SceneSelector - Visual scene picker for the configurator
 */
import { SCENES, type SceneId } from '@ui/hooks/usePrestige';

type SceneSelectorProps = {
  currentScene: SceneId;
  onSceneChange: (sceneId: SceneId) => void;
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
};

/* Simple SVG icons for each scene */

const SkyIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    {/* Sun */}
    <circle cx="16" cy="13" r="5" fill="#f5c542" />
    <line x1="16" y1="4" x2="16" y2="6" stroke="#f5c542" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="16" y1="20" x2="16" y2="22" stroke="#f5c542" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="7" y1="13" x2="9" y2="13" stroke="#f5c542" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="23" y1="13" x2="25" y2="13" stroke="#f5c542" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="9.34" y1="6.34" x2="10.76" y2="7.76" stroke="#f5c542" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="21.24" y1="18.24" x2="22.66" y2="19.66" stroke="#f5c542" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="22.66" y1="6.34" x2="21.24" y2="7.76" stroke="#f5c542" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="10.76" y1="18.24" x2="9.34" y2="19.66" stroke="#f5c542" strokeWidth="1.5" strokeLinecap="round" />
    {/* Ground */}
    <path d="M2 26 Q8 24 16 26 Q24 28 30 26" stroke="#8cb369" strokeWidth="1.5" fill="none" strokeLinecap="round" />
  </svg>
);

const CourtyardIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    {/* Floor tiles */}
    <rect x="4" y="22" width="24" height="6" rx="1" fill="#e8dcc8" />
    <line x1="10" y1="22" x2="10" y2="28" stroke="#d4c5a9" strokeWidth="0.8" />
    <line x1="16" y1="22" x2="16" y2="28" stroke="#d4c5a9" strokeWidth="0.8" />
    <line x1="22" y1="22" x2="22" y2="28" stroke="#d4c5a9" strokeWidth="0.8" />
    {/* Walls */}
    <rect x="2" y="6" width="3" height="16" rx="1" fill="#bbb" />
    <rect x="27" y="6" width="3" height="16" rx="1" fill="#bbb" />
    {/* Sky */}
    <circle cx="22" cy="10" r="3" fill="#f5c542" opacity="0.7" />
    {/* Plant */}
    <ellipse cx="7" cy="20" rx="2" ry="3" fill="#8cb369" />
    <rect x="6.5" y="21" width="1" height="2" fill="#7a6c5b" />
  </svg>
);

const WallIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    {/* Wall */}
    <rect x="2" y="4" width="28" height="20" rx="2" fill="#ccc" />
    {/* Bricks */}
    <line x1="2" y1="10" x2="30" y2="10" stroke="#bbb" strokeWidth="0.8" />
    <line x1="2" y1="16" x2="30" y2="16" stroke="#bbb" strokeWidth="0.8" />
    <line x1="10" y1="4" x2="10" y2="10" stroke="#bbb" strokeWidth="0.8" />
    <line x1="22" y1="4" x2="22" y2="10" stroke="#bbb" strokeWidth="0.8" />
    <line x1="16" y1="10" x2="16" y2="16" stroke="#bbb" strokeWidth="0.8" />
    <line x1="6" y1="16" x2="6" y2="24" stroke="#bbb" strokeWidth="0.8" />
    <line x1="26" y1="16" x2="26" y2="24" stroke="#bbb" strokeWidth="0.8" />
    {/* Floor */}
    <rect x="2" y="24" width="28" height="4" rx="1" fill="#e8dcc8" />
  </svg>
);

const SCENE_OPTIONS: {
  id: SceneId;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    id: null,
    label: 'Default Sky',
    description: 'Open sky environment',
    icon: <SkyIcon />,
  },
  ...SCENES.map((scene) => ({
    id: scene.id as SceneId,
    label: scene.name,
    description:
      scene.id === 'courtyard'
        ? 'Outdoor courtyard setting'
        : 'Wall-mounted backdrop',
    icon: scene.id === 'courtyard' ? <CourtyardIcon /> : <WallIcon />,
  })),
];

export const SceneSelector = ({
  currentScene,
  onSceneChange,
  visible,
  onVisibleChange,
}: SceneSelectorProps) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── Heading ── */}
      <p style={{ textAlign: 'center', fontSize: 15, color: '#444', margin: 0 }}>
        Choose your <strong>scene</strong>:
      </p>

      {/* ── Optional visibility toggle ── */}
      {onVisibleChange && typeof visible === 'boolean' && (
        <button
          type="button"
          onClick={() => onVisibleChange(!visible)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '10px 14px',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            background: '#fafafa',
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 500, color: '#444' }}>
            Show Scene Preview
          </span>
          <div
            style={{
              width: 40,
              height: 22,
              borderRadius: 11,
              background: visible ? '#71903e' : '#ccc',
              position: 'relative',
              transition: 'background 0.2s',
            }}
          >
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: '#fff',
                position: 'absolute',
                top: 3,
                left: visible ? 21 : 3,
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }}
            />
          </div>
        </button>
      )}

      {/* ── Scene cards ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {SCENE_OPTIONS.map((option) => {
          const isSelected = currentScene === option.id;
          return (
            <button
              key={option.id ?? 'default'}
              type="button"
              onClick={() => onSceneChange(option.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '12px 14px',
                border: isSelected
                  ? '2px solid #71903e'
                  : '1.5px solid #e0e0e0',
                borderRadius: 10,
                background: isSelected ? 'rgba(113,144,62,0.05)' : '#fff',
                cursor: 'pointer',
                transition: 'border-color 0.2s, background 0.2s',
                width: '100%',
                textAlign: 'left',
              }}
            >
              {/* Icon */}
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 10,
                  background: isSelected ? 'rgba(113,144,62,0.1)' : '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'background 0.2s',
                }}
              >
                {option.icon}
              </div>

              {/* Text */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: isSelected ? 700 : 500,
                    color: isSelected ? '#333' : '#555',
                    transition: 'color 0.2s',
                  }}
                >
                  {option.label}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: '#999',
                    marginTop: 2,
                  }}
                >
                  {option.description}
                </div>
              </div>

              {/* Check indicator */}
              {isSelected && (
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: '#71903e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2.5 6L5 8.5L9.5 3.5"
                      stroke="#fff"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
